import React, { useState, useContext, useEffect, useRef } from 'react';
import { Form, Radio, Space, Input, Button, Divider, Alert } from 'antd';
import { AppContext } from '../../../context/ContextProvider';
import { AlertContext } from '../../../context/alertContext';
import api from '../../../api';
import { formatErrors } from '../../../context/helper';
import CreateTaskHeader from '../../ScrumBoard/Task/TaskCreateOrUpdate/CreateTaskHeader/CreateTaskHeader';
import Scrollbars from 'react-custom-scrollbars-2';
import CreateTaskFooter from '../../ScrumBoard/Task/TaskCreateOrUpdate/CreateTaskFooter/CreateTaskFooter';


export default function AwsIntegration({ data, connected, setConnected, load, setLoad, uniqueIdentifier, myAssumeRole, timerRef }) {
  const { setOpenRightDrawer } = useContext(AppContext);
  const { set_alertOptions } = useContext(AlertContext);
  const [form] = Form.useForm();
  const [authType, setAuthType] = useState('role');
  const [loading, setLoading] = useState(false);
  const pollingTimeoutRef = useRef(null);

  const handleCancel = () => {
    setOpenRightDrawer({ drawerType: false, drawerProps: { data: "" } });
  };

  // Clear only the interval when drawer unmounts (e.g. user closed before 30s).
  // Do NOT clear the 30s timeout so polling can still start after Connect + drawer close.
  useEffect(() => {
    return () => {
      if (timerRef?.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [timerRef]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        auth_type: authType,
        external_id: authType === 'role' ? uniqueIdentifier : undefined,
      };

      const response = await api.post('/api/integration/account/', payload);
      
      if (response.data.status) {
        set_alertOptions({
          status: true,
          type: "success",
          message: "Success",
          description: "AWS Account integrated successfully.",
        });
        setConnected(!connected);
        handleCancel();
      } else {
        throw new Error(formatErrors(response.data.error_details));
      }
    } catch (error) {
      set_alertOptions({
        status: true,
        type: "error",
        message: "Integration Failed",
        description: error.message || "Something went wrong.",
      });
      
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        set_alertOptions(prev => ({
          ...prev,
          status: false,
        }));
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleIntegration = async () => {
    // Standard flow: Save unique identifier then open CloudFormation
    try {
      setLoading(true);
      const payload = {
        integration: data.id,
        unique_identifier: uniqueIdentifier
      };
      
      const res = await api.post("/api/integration/uniqueidentifier/", payload);
      if (res.data.status) {
        // Start polling AWS integration status after 30s, then every 5s (schedule before closing drawer)
        if (myAssumeRole && timerRef) {
          pollingTimeoutRef.current = setTimeout(() => {
            if (timerRef.current) clearInterval(timerRef.current);
            timerRef.current = setInterval(() => {
              myAssumeRole();
            }, 5000);
          }, 1000 * 30);
        }
        window.open(`https://${process.env.REACT_APP_AWS_REGION}.console.aws.amazon.com/cloudformation/home?region=${process.env.REACT_APP_AWS_REGION}#/stacks/create/review?templateURL=${process.env.REACT_APP_STACK_TEMPLATE_URL}&param_ATMOSLYIdentifier=${uniqueIdentifier}&stackName=${process.env.REACT_APP_CLOUDFORMATION_STACK_NAME}`, '_blank');
        handleCancel();
        set_alertOptions({
          status: true,
          type: "info",
          message: "Action Required",
          description: "Please complete the CloudFormation stack creation in the new tab.",
        });
      } else {
        throw new Error(formatErrors(res.data.error_details));
      }
    } catch (error) {
      set_alertOptions({
        status: true,
        type: "error",
        message: "Failed",
        description: error.message || "Could not initialize role integration.",
      });
      
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        set_alertOptions(prev => ({
          ...prev,
          status: false,
        }));
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      <CreateTaskHeader buttonContent="AWS Integration" onCancel={handleCancel} />
      
      <Scrollbars style={{ flex: 1, marginTop: '84px', marginBottom: '84px' }}>
        <div style={{ padding: '24px' }}>
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item label="Authentication Method">
              <Radio.Group value={authType} onChange={(e) => setAuthType(e.target.value)}>
                <Space direction="vertical">
                  <Radio value="role">
                    <b>IAM Role (Recommended)</b>
                    <div style={{ fontSize: '12px', color: '#888' }}>Use cross-account roles for secure, long-term access.</div>
                  </Radio>
                  <Radio value="keys">
                    <b>Access Keys (Temporary/Direct)</b>
                    <div style={{ fontSize: '12px', color: '#888' }}>Use IAM user access keys for quick or temporary access.</div>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            <Divider />

            {authType === 'role' ? (
              <div style={{ padding: '20px 0' }}>
                <p>To use this method, Atmosly needs permission to access your account via an IAM Role. We provide a CloudFormation template to automate this setup.</p>
                <Alert 
                  type="info" 
                  message="How it works" 
                  description="Clicking 'Connect' will open the AWS Console in a new tab with a pre-filled CloudFormation stack. Once you create the stack, Atmosly will be automatically linked." 
                />
              </div>
            ) : (
              <>
                <Form.Item name="access_key_id" label="Access Key ID" rules={[{ required: true, message: 'Required' }]}>
                  <Input placeholder="AKIA..." />
                </Form.Item>

                <Form.Item name="secret_access_key" label="Secret Access Key" rules={[{ required: true, message: 'Required' }]}>
                  <Input.Password placeholder="Secret Key" />
                </Form.Item>

                <Alert 
                  type="warning" 
                  showIcon 
                  message="Security Warning" 
                  description="Access keys provide long-term access. For production environments, we strongly recommend using the IAM Role method." 
                  style={{ marginBottom: '24px' }}
                />
              </>
            )}
          </Form>
        </div>
      </Scrollbars>

      <CreateTaskFooter 
        buttonContent="AWSIntegration"
        onCancel={handleCancel} 
        givenButtonFunction1={authType === 'role' ? handleRoleIntegration : () => form.submit()} 
        givenButtonFunction2={handleCancel}
        loading={loading}
      />
    </div>
  );
}
